import { Colors } from '@factorialco/deprecated-design-system'
import {
  BulkCreateShiftmanagementShiftShiftsInput,
  BulkCreateShiftmanagementUnassignedShiftUnassignedShiftsInput,
} from 'generated/resources/graphql'
import first from 'lodash/first'
import uniq from 'lodash/uniq'
import moment from 'moment'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'

import { SYMBOLIC_MIXED_VALUE } from 'modules/shift_management/lib/types'
import type Shift from 'modules/shift_management/store/internal/models/Shift'
import type UnassignedShift from 'modules/shift_management/store/internal/models/UnassignedShift'
import type View from 'modules/shift_management/store/internal/models/View'
import type ViewOptions from 'modules/shift_management/store/internal/models/ViewOptions'

import { AddShiftOptionType } from './addShiftOptions'
import { nameFromHexCode } from './colors'

const AMPLITUDE_SHIFT_TYPES: Record<AddShiftOptionType, string> = Object.freeze({
  defaultShift: 'default',
  customShift: 'custom',
  template: 'template',
  copyPeriod: 'copy_period',
  savedCustom: 'saved-custom',
  duplicate: 'duplicate-drag-and-drop',
})

const trackAddMutationEvent = ({
  view,
  isDefaultShift,
  weekDays,
  shifts,
  isBackup,
  defaultShiftsCount,
  from,
  withSearch,
  advancedOptions,
  colors,
}: {
  view: View
  isDefaultShift: boolean | undefined
  weekDays: string
  shifts: BulkCreateShiftmanagementShiftShiftsInput[]
  isBackup?: boolean
  defaultShiftsCount?: number
  from?: 'modal' | 'side_panel' | 'quick_actions' | 'drag_and_drop' | 'copy_period'
  withSearch?: string
  advancedOptions?: boolean
  colors?: string[]
}) => {
  const cellCount = shifts.length
  const shiftType = isDefaultShift ? 'defaultShift' : 'customShift'

  const shiftData = shifts.reduce(
    (acc, shift) => {
      acc.employeeIds.add(Number(shift.employeeId))
      acc.names.add(shift.name || '')
      acc.startAt.add(shift.startAt)
      acc.endAt.add(shift.endAt)
      acc.extraHours.add(!!shift.extraHours)
      return acc
    },
    {
      employeeIds: new Set<number>(),
      names: new Set<string>(),
      startAt: new Set<string>(),
      endAt: new Set<string>(),
      extraHours: new Set<boolean>(),
    }
  )

  const employeesSelected = Array.from(shiftData.employeeIds)
  const name = Array.from(shiftData.names)
  const startAt = Array.from(shiftData.startAt)
  const endAt = Array.from(shiftData.endAt)
  const extraHours = Array.from(shiftData.extraHours)

  const colorSet = new Set(colors?.map((c) => nameFromHexCode(c)))
  const color = Array.from(colorSet)

  const extras = {
    shift_name: name,
    shift_start_time: startAt,
    shift_end_time: endAt,
    overtime_flag: !!extraHours,
    selected_days: weekDays,
    number_of_different_times_added: defaultShiftsCount,
  }

  trackAddShiftSubmitEvent({
    view,
    employeeIds: employeesSelected,
    shiftsCount: cellCount,
    restDaysCount: 0,
    shiftType,
    color,
    isBackup,
    from,
    withSearch,
    advancedOptions,
    extras,
  })
}

// TODO: Same as the above but with unassigned shifts :_D
//
const trackAddUnassignedEvent = ({
  unassignedShiftParams,
  isDefaultShift,
  colors,
}: {
  unassignedShiftParams: BulkCreateShiftmanagementUnassignedShiftUnassignedShiftsInput[]
  isDefaultShift: boolean
  colors?: string[]
}) => {
  const shiftType = isDefaultShift ? 'defaultShift' : 'customShift'
  const counter = Number(first(unassignedShiftParams.map((s) => s.counter)))

  const startAt = uniq(unassignedShiftParams.map((s) => s.startAt))
  const endAt = uniq(unassignedShiftParams.map((s) => s.endAt))
  const extraHours = uniq(unassignedShiftParams.map((s) => s.extraHours))

  const allCompany = unassignedShiftParams.map((s) => s.allCompany)
  const teamVisibilities = unassignedShiftParams.flatMap((s) => Number(s.teamsIdsVisibilities))
  const locationVisibilities = unassignedShiftParams.flatMap((s) =>
    Number(s.locationsIdsVisibilities)
  )

  const extras = {
    shift_start_time: startAt,
    shift_end_time: endAt,
    overtime_flag: !!extraHours,
  }

  trackAddUnassignedShiftSubmit(
    counter,
    shiftType,
    allCompany,
    teamVisibilities,
    locationVisibilities,
    colors?.map((c) => nameFromHexCode(c)) || [],
    extras
  )
}

const trackAddShiftSubmitEvent = ({
  view,
  employeeIds,
  shiftsCount,
  restDaysCount,
  shiftType,
  color,
  isBackup,
  from,
  withSearch,
  advancedOptions,
  extras,
}: {
  view: View
  employeeIds: number[]
  shiftsCount: number
  restDaysCount: number
  shiftType: AddShiftOptionType
  color: Colors | null | (Colors | null)[]
  isBackup?: boolean
  from?: 'modal' | 'side_panel' | 'quick_actions' | 'drag_and_drop' | 'copy_period' | 'coverage'
  withSearch?: string
  advancedOptions?: boolean
  extras?: Record<string, string | number | boolean | string[] | undefined>
}) => {
  track(trackingEvents.shiftManagement.addShiftSubmit, {
    number_of_impacted_employees: employeeIds.length,
    number_of_impacted_shifts: shiftsCount,
    number_of_impacted_rest_days: restDaysCount,
    view: view.viewType,
    view_type_by: view.groupType,
    shift_type: AMPLITUDE_SHIFT_TYPES[shiftType],
    color,
    is_backup: isBackup,
    from,
    with_search: withSearch,
    advanced_options: advancedOptions ?? false,
    ...extras,
  })
}

const trackAddShiftStartEvent = ({
  view,
  impactedEmployees,
  cellCount,
  shiftType,
  from,
  isBackup,
  quickActions,
}: {
  view: View
  impactedEmployees: number
  cellCount: number
  shiftType: AddShiftOptionType
  from: 'top_bar' | 'contextual_button'
  isBackup?: boolean
  quickActions?: boolean
}) => {
  const trackingProperties = {
    number_of_impacted_employees: impactedEmployees,
    number_of_impacted_shifts: cellCount,
    view: view.viewType,
    view_type_by: view.groupType,
    shift_type: AMPLITUDE_SHIFT_TYPES[shiftType],
    from,
    is_backup: isBackup,
    quick_actions: quickActions ?? false,
  }

  track(trackingEvents.shiftManagement.addShiftStart, trackingProperties)
}

const trackAddDefaultShiftStart = ({
  daysPreselected,
  view,
  from,
}: {
  daysPreselected: boolean
  view?: View
  from: 'modal' | 'settings' | 'save_shift_toggle'
}) => {
  const trackingProperties = {
    days_preselected: daysPreselected,
    view: view?.viewType,
    view_type_by: view?.groupType,
    from,
  }
  track(trackingEvents.shiftManagement.addNewSavedShiftStart, trackingProperties)
}

const trackAddItemToShiftScheduleStart = ({
  impactedEmployees,
  cellCount,
  view,
  from,
}: {
  impactedEmployees: number
  cellCount: number
  view: View
  from: 'contextual_button' | 'top_bar'
}) => {
  const trackingProperties = {
    number_of_impacted_employees: impactedEmployees,
    number_of_impacted_shifts: cellCount,
    view: view.viewType,
    view_type_by: view.groupType,
    from,
  }
  return track(trackingEvents.shiftManagement.addItemToShiftsScheduleStart, trackingProperties)
}

interface TrackProps {
  name: string | string[]
  color: Colors | null | (Colors | null)[]
  startAt: string | string[]
  endAt: string | string[]
  workplace?: number | null | (number | null)[]
  workarea?: number | null | (number | null)[]
  comments?: string | null | (string | null)[]
  allCompany?: boolean | null
  teamsIdsVisibilities?: number[] | null
  locationsIdsVisibilities?: number[] | null
  view?: View
  daysPreselected?: boolean
  from?: 'settings' | 'save_shift_toggle'
}

const trackingProperties = ({
  name,
  color,
  startAt,
  endAt,
  workplace,
  workarea,
  comments,
  allCompany,
  teamsIdsVisibilities,
  locationsIdsVisibilities,
  view,
  daysPreselected,
  from,
}: TrackProps) => {
  const daysProperty = daysPreselected ? { days_preselected: daysPreselected } : {}

  const teamVisibilities =
    teamsIdsVisibilities?.length && !allCompany ? Array.from(teamsIdsVisibilities) : null
  const locationVisibilities =
    locationsIdsVisibilities?.length && !allCompany ? Array.from(locationsIdsVisibilities) : null

  return {
    name,
    color,
    start_at: startAt,
    end_at: endAt,
    workplace: workplace,
    workarea: workarea,
    comments: comments,
    all_company_saved_shift: allCompany,
    teams_ids_visibilities: teamVisibilities,
    locations_ids_visibilities: locationVisibilities,
    view: view?.viewType,
    view_type_by: view?.groupType,
    from: from,
    ...daysProperty,
  }
}

const trackAddDefaultShiftSubmit = (props: TrackProps) => {
  track(trackingEvents.shiftManagement.addNewSavedShiftSubmit, trackingProperties(props))
}

const trackEditDefaultShiftStart = (props: TrackProps) => {
  track(trackingEvents.shiftManagement.editNewSavedShiftStart, trackingProperties(props))
}

const trackEditDefaultShiftSubmit = (props: TrackProps) => {
  track(trackingEvents.shiftManagement.editNewSavedShiftSubmit, trackingProperties(props))
}

const trackDeleteDefaultShiftStart = (props: TrackProps) => {
  track(trackingEvents.shiftManagement.deleteNewSavedShiftStart, trackingProperties(props))
}

const trackDeleteDefaultShiftSubmit = (props: TrackProps) => {
  track(trackingEvents.shiftManagement.deleteNewSavedShiftSubmit, trackingProperties(props))
}

const trackAddTemplateShiftManagementStart = () => {
  track(trackingEvents.shiftManagement.addTemplateShiftManagementStart, {})
}

const trackAddTemplateWeekShiftManagementStart = (templateId: number) => {
  const properties = { template_id: templateId }

  track(trackingEvents.shiftManagement.addTemplateWeekShiftManagementStart, properties)
}

interface TemplateProps {
  templateId: number
  weekId: number
  workingDays: number
  restDays: number
  emptyDays: number
  color: Colors | null
}

const trackAddTemplateWeekShiftManagementSubmit = ({
  templateId,
  weekId,
  workingDays,
  restDays,
  emptyDays,
  color,
}: TemplateProps) => {
  const properties = {
    template_id: templateId,
    week_id: weekId,
    working_days: workingDays,
    rest_days: restDays,
    empty_days: emptyDays,
    color,
  }

  track(trackingEvents.shiftManagement.addTemplateWeekShiftManagementSubmit, properties)
}

const trackAddCopyShiftsToAllTemplateShiftManagement = ({
  templateId,
  weekId,
  workingDays,
  restDays,
  emptyDays,
  color,
}: TemplateProps) => {
  const properties = {
    template_id: templateId,
    week_id: weekId,
    working_days: workingDays,
    rest_days: restDays,
    empty_days: emptyDays,
    color,
  }

  track(trackingEvents.shiftManagement.addCopyShiftsToAllTemplateShiftManagement, properties)
}

const trackDeleteTemplateWeekShiftManagementSubmit = (templateId: number, weekId: number) => {
  const properties = { template_id: templateId, week_id: weekId }

  track(trackingEvents.shiftManagement.deleteTemplateWeekShiftManagementSubmit, properties)
}

const trackDeleteTemplateShiftManagementSubmit = (templateId: number) => {
  const properties = { template_id: templateId }

  track(trackingEvents.shiftManagement.deleteTemplateShiftManagementSubmit, properties)
}

const trackGoToShiftsContextualMenu = (view: View) => {
  const properties = { view: view.viewType, view_type_by: view.groupType }

  track(trackingEvents.shiftManagement.goToShiftsContextualMenu, properties)
}

const trackGoToDetailsShiftStart = ({
  from,
  view,
  isBackup,
}: {
  from: string
  view: View
  isBackup?: boolean
}) => {
  const properties = {
    view: view.viewType,
    view_type_by: view.groupType,
    is_backup: isBackup,
    from,
  }

  track(trackingEvents.shiftManagement.goToDetailsShiftStart, properties)
}

const trackGoToDetailsShiftSubmit = (permissions: string, view: View) => {
  const properties = { view: view.viewType, view_type_by: view.groupType }

  track(trackingEvents.shiftManagement.goToDetailsShiftSubmit, properties)
}
const trackDeleteOneShiftStart = ({
  shift,
  view,
  from,
  isBackup,
}: {
  shift: Shift
  view: View
  from: string
  isBackup?: boolean
}) => {
  const properties = {
    number_of_impacted_shifts: 1,
    number_of_impacted_employees: 1,
    number_of_impacted_rest_days: 0,
    shift_ids: [shift.id],
    view: view.viewType,
    view_type_by: view.groupType,
    from: from,
    is_backup: isBackup,
  }

  track(trackingEvents.shiftManagement.deleteShiftStart, properties)
}

const trackEditOneShiftStart = (
  shift: Shift,
  view: View,
  from: string,
  shiftType: AddShiftOptionType | null
) => {
  const properties = {
    number_of_impacted_shifts: 1,
    number_of_impacted_employees: 1,
    shift_ids: [shift.id],
    view: view.viewType,
    view_type_by: view.groupType,
    from: from,
    overtime_flag: shift.isOvertime,
    workplace: shift.location?.name,
    workarea: shift.workArea?.name,
    comment: shift.notes,
    color: shift.color,
    shift_type: shiftType ? AMPLITUDE_SHIFT_TYPES[shiftType] : null,
  }

  track(trackingEvents.shiftManagement.editShiftStart, properties)
}

interface TrackEditShiftSubmitProps {
  shifts: Shift[]
  name: string | null
  startAt: string | null
  endAt: string | null
  notes: string | null
  extraHours: boolean | null
  locationId: number | null
  workAreaId: number | null
  mixedValueNotes: number
  view: View
  assignedToModified: boolean
  originalEmployeeIncluded: boolean
  totalNumberOfAssignedEmployees: number
  through: 'modal' | 'drag-and-drop' | 'side_panel'
  replacedShifts?: boolean
  date?: string | null
}

const trackEditShiftSubmit = ({
  shifts,
  name,
  startAt,
  endAt,
  notes,
  extraHours,
  locationId,
  workAreaId,
  mixedValueNotes,
  view,
  assignedToModified,
  originalEmployeeIncluded,
  totalNumberOfAssignedEmployees,
  through,
  replacedShifts = false,
  date = null,
}: TrackEditShiftSubmitProps) => {
  const employeesShifts = uniq(shifts.map((s) => s.employeeId))

  // We have to separate the checks below since in some cases null can mean both
  // empty or mixed values.
  const mixedByNull = [name, startAt, endAt, extraHours].filter((e) => e === null).length
  const mixedByNumber = [Number(locationId), Number(workAreaId)].filter(
    (e) => e === SYMBOLIC_MIXED_VALUE
  ).length

  const numberOfFieldsWithMixedValues = mixedValueNotes + mixedByNull + mixedByNumber
  const shiftType = shifts.map((s) => s.typeForEvents)

  return track(trackingEvents.shiftManagement.editShiftSubmit, {
    number_of_impacted_shifts: shifts.length,
    number_of_impacted_employees: employeesShifts.length,
    shift_ids: shifts.map((s) => s.id),
    number_of_fields_with_mixed_values: numberOfFieldsWithMixedValues,
    name: name,
    shift_start_time: startAt,
    shift_end_time: endAt,
    overtime_flag: extraHours,
    workplace: locationId,
    workarea: workAreaId,
    comment: notes,
    view: view.viewType,
    view_type_by: view.groupType,
    color: shifts.map((s) => s.color),
    shift_type: shiftType,
    assigned_to_modified: assignedToModified,
    original_employee_included: originalEmployeeIncluded,
    total_number_of_assigned_employees: totalNumberOfAssignedEmployees,
    through: through,
    replace_shifts_used: replacedShifts,
    day_modified: shifts.map((s) => dateToString(s.date, 'backend')).every((d) => d === date),
  })
}

const trackAddUnassignedShiftSubmit = (
  counter: number,
  shiftType: AddShiftOptionType,
  allCompany: boolean[],
  teamsIdsVisibilities: number[],
  locationsIdsVisibilities: number[],
  colors: Colors | null | (Colors | null)[],
  extras?: Record<string, string | number | boolean | string[] | undefined>
) => {
  const properties = {
    number_of_employees_needed: counter,
    shift_type: AMPLITUDE_SHIFT_TYPES[shiftType],
    all_company: allCompany,
    team_ids_visibility: teamsIdsVisibilities,
    location_ids_visibility: locationsIdsVisibilities,
    colors,
    ...extras,
  }

  track(trackingEvents.shiftManagement.addUnassignedShiftSubmit, properties)
}

const trackEditUnassignedShiftSubmit = (
  unassignedShift: UnassignedShift,
  shiftType: AddShiftOptionType,
  counter: number,
  allCompany: boolean,
  teamsIdsVisibilities: number[],
  locationsIdsVisibilities: number[],
  extras?: Record<string, string | number | boolean | string[] | undefined>
) => {
  const properties = {
    unassigned_shift_id: unassignedShift.id,
    number_of_employees_needed: counter,
    shift_type: AMPLITUDE_SHIFT_TYPES[shiftType],
    all_company: allCompany,
    team_ids_visibility: teamsIdsVisibilities,
    location_ids_visibility: locationsIdsVisibilities,
    color: unassignedShift.color,
    ...extras,
  }

  track(trackingEvents.shiftManagement.editUnassignedShiftSubmit, properties)
}

const trackDeleteUnassignedShiftSubmit = (
  unassignedShift: UnassignedShift,
  shiftType: AddShiftOptionType,
  counter: number,
  from: string
) => {
  const properties = {
    unassigned_shift_id: unassignedShift.id,
    shift_type: AMPLITUDE_SHIFT_TYPES[shiftType],
    number_of_employees_needed: counter,
    from: from,
  }

  track(trackingEvents.shiftManagement.deleteUnassignedShiftSubmit, properties)
}

const trackAssignEmployeesUnassignedShiftSubmit = (
  counter: number,
  impactedEmployees: number,
  from: string
) => {
  const properties = {
    number_of_employees_needed: counter,
    number_of_impacted_employees: impactedEmployees,
    from: from,
  }

  track(trackingEvents.shiftManagement.assignEmployeesUnassignedShiftSubmit, properties)
}

const trackYearlyExportSubmit = (
  startDate: string,
  endDate: string,
  teamsIds: number[],
  locationsIds: number[],
  from: string
) => {
  const checkRangeInTime = (): string => {
    const today = moment()
    const start = moment(startDate)
    const end = moment(endDate)

    const isTodayWithinRange = today.isBetween(start, end, 'day', '[]')
    if (isTodayWithinRange) {
      return 'present'
    }

    const isRangeInPast = start.isBefore(today)
    if (isRangeInPast) {
      return 'past'
    }

    return 'future'
  }

  const properties = {
    teams_selected: !!teamsIds.length,
    number_of_teams_selected: teamsIds.length,
    workplaces_selected: !!locationsIds.length,
    number_of_workplaces_selected: locationsIds.length,
    export_from: from,
    when_is_export_for: checkRangeInTime(),
  }

  track(trackingEvents.shiftManagement.requestYearlyExportSubmit, properties)
}

const trackOrderEmployees = (orderType: string, orderDir: string) => {
  const properties = {
    order_type: orderType,
    order_dir: orderDir,
  }

  track(trackingEvents.shiftManagement.orderEmployees, properties)
}

const trackOpenTimeInsightsSidepanelSubmit = (teamsIds: number[], locationsIds: number[]) => {
  const properties = {
    teams_selected: !!teamsIds.length,
    number_of_teams_selected: teamsIds.length,
    workplaces_selected: !!locationsIds.length,
    number_of_workplaces_selected: locationsIds.length,
  }

  track(trackingEvents.shiftManagement.openTimeInsightsSidepanelSubmit, properties)
}

const trackViewOptionsEvent = (
  isOpen: boolean,
  view: View,
  viewOptions: ViewOptions,
  hasCoverage: boolean
) => {
  const properties = {
    view: view.viewType,
    view_type_by: view.groupType,
  }

  if (isOpen) {
    track(trackingEvents.shiftManagement.viewOptionsStart, properties)
  } else {
    const { employee, schedule, coverage } = viewOptions
    track(trackingEvents.shiftManagement.viewOptionsSubmit, {
      ...properties,
      section: 'employee',
      section_details_names: Object.keys(employee),
      section_details_values: Object.values(employee),
    })
    track(trackingEvents.shiftManagement.viewOptionsSubmit, {
      ...properties,
      section: 'schedule',
      section_details_names: Object.keys(schedule),
      section_details_values: Object.values(schedule),
    })
    if (hasCoverage) {
      track(trackingEvents.shiftManagement.viewOptionsSubmit, {
        ...properties,
        section: 'coverage',
        section_details_names: Object.keys(coverage),
        section_details_values: Object.values(coverage),
      })
    }
  }
}

const trackOpenSwapRequestApproval = (openFrom: 'Inbox' | 'Modal') => {
  const properties = {
    open_from: openFrom,
  }

  track(trackingEvents.shiftManagement.openSwapRequestApproval, properties)
}

const trackSubmitSwapRequestApproval = (requestAccepted: boolean, reason?: string) => {
  const properties = {
    request_accepted: requestAccepted,
    reason: reason,
  }

  track(trackingEvents.shiftManagement.submitSwapRequestApproval, properties)
}

const trackAddTimeOffFromShiftStartEvent = ({ view }: { view: View }) => {
  const properties = {
    view: view.viewType,
    view_type_by: view.groupType,
  }

  track(trackingEvents.shiftManagement.addTimeOffFromShiftStart, properties)
}

const trackEditTimeOffFromShiftStartEvent = ({
  view,
  isApproved,
}: {
  view: View
  isApproved: boolean
}) => {
  const properties = {
    view: view.viewType,
    view_type_by: view.groupType,
    is_approved: isApproved,
  }

  track(trackingEvents.shiftManagement.editTimeOffFromShiftStart, properties)
}

export {
  trackAddCopyShiftsToAllTemplateShiftManagement,
  trackAddDefaultShiftStart,
  trackAddDefaultShiftSubmit,
  trackAddMutationEvent,
  trackAddItemToShiftScheduleStart,
  trackAddShiftStartEvent,
  trackAddShiftSubmitEvent,
  trackAddTemplateShiftManagementStart,
  trackAddTemplateWeekShiftManagementStart,
  trackAddTemplateWeekShiftManagementSubmit,
  trackAddTimeOffFromShiftStartEvent,
  trackAddUnassignedEvent,
  trackAssignEmployeesUnassignedShiftSubmit,
  trackDeleteDefaultShiftStart,
  trackDeleteDefaultShiftSubmit,
  trackDeleteOneShiftStart,
  trackDeleteTemplateShiftManagementSubmit,
  trackDeleteTemplateWeekShiftManagementSubmit,
  trackDeleteUnassignedShiftSubmit,
  trackEditDefaultShiftStart,
  trackEditDefaultShiftSubmit,
  trackEditOneShiftStart,
  trackEditShiftSubmit,
  trackEditTimeOffFromShiftStartEvent,
  trackEditUnassignedShiftSubmit,
  trackGoToDetailsShiftStart,
  trackGoToDetailsShiftSubmit,
  trackGoToShiftsContextualMenu,
  trackOpenSwapRequestApproval,
  trackOpenTimeInsightsSidepanelSubmit,
  trackOrderEmployees,
  trackSubmitSwapRequestApproval,
  trackViewOptionsEvent,
  trackYearlyExportSubmit,
}
